const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Format a Date as local calendar yyyy-MM-dd. */
export function formatCalendarDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Today's date in the local calendar as yyyy-MM-dd. */
export function todayCalendarDate() {
  return formatCalendarDate(new Date());
}

/** Local calendar yyyy-MM-dd for a date N days before today. */
export function calendarDateDaysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatCalendarDate(date);
}

/** Local calendar yyyy-MM-dd from an ISO timestamp or Date. */
export function calendarDateFromInstant(value) {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return '';
  }
  return formatCalendarDate(date);
}

/** ISO-8601 UTC midnight instant for a yyyy-MM-dd calendar date. */
export function calendarDateToIsoInstant(dateString) {
  const epochMillis = calendarDateToEpochMillis(dateString);
  if (epochMillis == null) {
    return null;
  }
  return new Date(epochMillis).toISOString();
}

/** Parse yyyy-MM-dd as a local calendar date (midnight local time). */
export function parseCalendarDate(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const match = dateString.match(CALENDAR_DATE_PATTERN);
  if (!match) {
    return null;
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year
    || date.getMonth() !== month
    || date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

/** True when dateString is a valid yyyy-MM-dd calendar date. */
export function isValidCalendarDate(dateString) {
  return parseCalendarDate(dateString) !== null;
}

/**
 * Convert yyyy-MM-dd to UTC midnight epoch millis for API payloads.
 * The instant represents the intended calendar date regardless of local TZ.
 */
export function calendarDateToEpochMillis(dateString) {
  if (!isValidCalendarDate(dateString)) {
    return null;
  }

  const [year, month, day] = dateString.split('-').map((part) => parseInt(part, 10));
  return Date.UTC(year, month - 1, day);
}

/** True when an instant falls within an inclusive calendar-date range. */
export function isInstantInCalendarDateRange(instant, startDate, endDate) {
  const dateValue = instant instanceof Date ? instant : new Date(instant);
  if (isNaN(dateValue.getTime())) {
    return false;
  }

  if (startDate) {
    const fromDate = parseCalendarDate(startDate);
    if (!fromDate) return false;
    if (dateValue < fromDate) return false;
  }

  if (endDate) {
    const toDateExclusive = parseCalendarDate(endDate);
    if (!toDateExclusive) return false;
    toDateExclusive.setDate(toDateExclusive.getDate() + 1);
    if (dateValue >= toDateExclusive) return false;
  }

  return true;
}

/**
 * yyyy-MM-dd that is `months` calendar months before `dateString`.
 * The day is clamped to the last valid day of the target month.
 */
export function calendarDateMonthsBefore(dateString, months) {
  const date = parseCalendarDate(dateString);
  if (!date || !Number.isInteger(months)) {
    return '';
  }

  const target = new Date(date.getFullYear(), date.getMonth() - months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDay));
  return formatCalendarDate(target);
}

/** Format yyyy-MM-dd as dd-MM-yyyy for display. */
export function formatCalendarDateDisplay(dateString) {
  if (!isValidCalendarDate(dateString)) {
    return '';
  }

  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
}
